import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useGameStatistics, formatStatistics } from "@/hooks/useGameStatistics";
import { useEventBasedStats, StatsPeriod } from "@/hooks/useEventBasedStats";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { StatsPeriodFilter } from "./StatsPeriodFilter";

interface GameStatisticsProps {
  fixtureId: number;
  homeTeamName: string;
  awayTeamName: string;
  homeTeamLogo?: string;
  awayTeamLogo?: string;
  homeTeamId?: string | null;
  awayTeamId?: string | null;
  gameStatus?: string;
  enablePolling?: boolean;
}

export function GameStatistics({ 
  fixtureId, 
  homeTeamName, 
  awayTeamName, 
  homeTeamLogo, 
  awayTeamLogo,
  homeTeamId,
  awayTeamId,
  gameStatus, 
  enablePolling = false 
}: GameStatisticsProps) {
  const { t } = useTranslation('gameDetails');
  const [period, setPeriod] = useState<StatsPeriod>('game');
  
  const { data: stats, isLoading: statsLoading, dataUpdatedAt } = useGameStatistics(fixtureId, gameStatus, enablePolling);
  const { stats: eventStats, isLoading: eventsLoading } = useEventBasedStats(
    fixtureId,
    period,
    homeTeamId ?? null,
    awayTeamId ?? null
  );
  
  const formatted = formatStatistics(stats);
  const isLive = gameStatus === 'live';
  const isHalfView = period === '1h' || period === '2h';
  const isLoading = statsLoading || (isHalfView && eventsLoading);

  if (isLoading) {
    return (
      <div className="space-y-3">
        <div className="animate-pulse space-y-4">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="h-12 bg-muted rounded" />
          ))}
        </div>
      </div>
    );
  }

  // For half views, we need event stats
  if (isHalfView && !eventStats) {
    return (
      <div className="space-y-3">
        <StatsPeriodFilter period={period} onChange={setPeriod} />
        <p className="text-sm text-muted-foreground text-center py-4">
          No events available for this period
        </p>
      </div>
    );
  }

  // For game view, we need formatted stats
  if (!isHalfView && !formatted) {
    return (
      <div className="space-y-3">
        <StatsPeriodFilter period={period} onChange={setPeriod} />
        <p className="text-sm text-muted-foreground text-center py-4">
          Statistics not available
        </p>
      </div>
    );
  }

  const StatRow = ({ 
    label, 
    homeValue, 
    awayValue 
  }: { 
    label: string; 
    homeValue: number; 
    awayValue: number; 
  }) => {
    const total = homeValue + awayValue || 1;
    const homePercentage = (homeValue / total) * 100;
    const awayPercentage = 100 - homePercentage;

    return (
      <div className="space-y-2">
        <div className="flex justify-between text-xs sm:text-sm">
          <span className="font-medium">{homeValue}</span>
          <span className="text-muted-foreground">{label}</span>
          <span className="font-medium">{awayValue}</span>
        </div>
        <div className="relative h-2.5 sm:h-3 bg-muted rounded-full overflow-hidden flex">
          <div 
            className="h-full bg-green-500 transition-all"
            style={{ width: `${homePercentage}%` }}
          />
          <div 
            className="h-full bg-red-500 transition-all"
            style={{ width: `${awayPercentage}%` }}
          />
        </div>
      </div>
    );
  };

  // Get period stats from JSONB if available
  const getPeriodData = () => {
    if (!stats?.statistics_periods || !isHalfView) return null;
    const periodKey = period; // '1h' or '2h'
    const p = (stats.statistics_periods as Record<string, Record<string, number | null>>)?.[periodKey];
    return p || null;
  };

  // Render half-time stats (event-based + period data from Sportmonks)
  const renderHalfStats = () => {
    if (!eventStats) return null;
    const periodData = getPeriodData();

    return (
      <div className="space-y-4">
        <StatRow 
          label="Goals" 
          homeValue={eventStats.goals.home} 
          awayValue={eventStats.goals.away} 
        />
        
        {/* Cards */}
        <div className="pt-2 border-t space-y-2">
          <div className="flex justify-between text-xs sm:text-sm">
            <div className="flex gap-2">
              <span className="font-medium">{eventStats.yellowCards.home}</span>
              {eventStats.redCards.home > 0 && (
                <span className="font-medium text-red-500">{eventStats.redCards.home}</span>
              )}
            </div>
            <span className="text-muted-foreground">Cards</span>
            <div className="flex gap-2">
              {eventStats.redCards.away > 0 && (
                <span className="font-medium text-red-500">{eventStats.redCards.away}</span>
              )}
              <span className="font-medium">{eventStats.yellowCards.away}</span>
            </div>
          </div>
          
          <div className="flex gap-2">
            <div className="flex-1 flex justify-start items-center gap-1">
              {Array.from({ length: eventStats.yellowCards.home }).map((_, i) => (
                <div key={`yellow-home-${i}`} className="w-2 h-3 sm:w-3 sm:h-4 bg-yellow-500 rounded-sm" />
              ))}
              {Array.from({ length: eventStats.redCards.home }).map((_, i) => (
                <div key={`red-home-${i}`} className="w-2 h-3 sm:w-3 sm:h-4 bg-red-500 rounded-sm" />
              ))}
            </div>
            <div className="flex-1 flex justify-end items-center gap-1">
              {Array.from({ length: eventStats.redCards.away }).map((_, i) => (
                <div key={`red-away-${i}`} className="w-2 h-3 sm:w-3 sm:h-4 bg-red-500 rounded-sm" />
              ))}
              {Array.from({ length: eventStats.yellowCards.away }).map((_, i) => (
                <div key={`yellow-away-${i}`} className="w-2 h-3 sm:w-3 sm:h-4 bg-yellow-500 rounded-sm" />
              ))}
            </div>
          </div>
        </div>

        <StatRow 
          label="Substitutions" 
          homeValue={eventStats.substitutions.home} 
          awayValue={eventStats.substitutions.away} 
        />

        {/* Period stats from Sportmonks (if available) */}
        {periodData ? (
          <div className="pt-2 border-t space-y-4">
            {/* Possession */}
            {periodData.home_ball_possession != null && (
              <div className="space-y-2">
                <div className="flex justify-between text-xs sm:text-sm">
                  <span className="font-medium">{periodData.home_ball_possession}%</span>
                  <span className="text-muted-foreground">Possession</span>
                  <span className="font-medium">{periodData.away_ball_possession}%</span>
                </div>
                <div className="relative h-2.5 sm:h-3 bg-muted rounded-full overflow-hidden flex">
                  <div className="h-full bg-green-500 transition-all" style={{ width: `${periodData.home_ball_possession}%` }} />
                  <div className="h-full bg-red-500 transition-all" style={{ width: `${periodData.away_ball_possession}%` }} />
                </div>
              </div>
            )}
            {periodData.home_total_shots != null && (
              <StatRow label="Shots" homeValue={periodData.home_total_shots || 0} awayValue={periodData.away_total_shots || 0} />
            )}
            {periodData.home_shots_on_goal != null && (
              <StatRow label="Shots on Target" homeValue={periodData.home_shots_on_goal || 0} awayValue={periodData.away_shots_on_goal || 0} />
            )}
            {periodData.home_corners != null && (
              <StatRow label="Corners" homeValue={periodData.home_corners || 0} awayValue={periodData.away_corners || 0} />
            )}
            {periodData.home_fouls != null && (
              <StatRow label="Fouls" homeValue={periodData.home_fouls || 0} awayValue={periodData.away_fouls || 0} />
            )}
            {periodData.home_attacks != null && (
              <StatRow label={t('stats.attacks', 'Attacks')} homeValue={periodData.home_attacks || 0} awayValue={periodData.away_attacks || 0} />
            )}
            {periodData.home_dangerous_attacks != null && (
              <StatRow label={t('stats.dangerousAttacks', 'Dangerous Attacks')} homeValue={periodData.home_dangerous_attacks || 0} awayValue={periodData.away_dangerous_attacks || 0} />
            )}
            {periodData.home_tackles != null && (
              <StatRow label={t('stats.tackles', 'Tackles')} homeValue={periodData.home_tackles || 0} awayValue={periodData.away_tackles || 0} />
            )}
            {periodData.home_interceptions != null && (
              <StatRow label={t('stats.interceptions', 'Interceptions')} homeValue={periodData.home_interceptions || 0} awayValue={periodData.away_interceptions || 0} />
            )}
            {periodData.home_key_passes != null && (
              <StatRow label={t('stats.keyPasses', 'Key Passes')} homeValue={periodData.home_key_passes || 0} awayValue={periodData.away_key_passes || 0} />
            )}
            {periodData.home_clearances != null && (
              <StatRow label={t('stats.clearances', 'Clearances')} homeValue={periodData.home_clearances || 0} awayValue={periodData.away_clearances || 0} />
            )}
          </div>
        ) : (
          <p className="text-xs text-muted-foreground text-center pt-2 border-t">
            Other statistics (possession, shots, etc.) are only available for the full game
          </p>
        )}
      </div>
    );
  };

  // Render full game stats
  const renderFullGameStats = () => {
    if (!formatted) return null;

    return (
      <div className="space-y-4">
        {/* xG (Expected Goals) - DESTAQUE EM AZUL NO TOPO */}
        {formatted.expectedGoals && (
          <div className="space-y-2 pb-4 border-b border-border">
            <div className="flex justify-between text-xs sm:text-sm">
              <span className="font-semibold text-blue-500">
                {formatted.expectedGoals.home.toFixed(2)}
              </span>
              <span className="text-muted-foreground font-medium">
                xG (Expected Goals)
              </span>
              <span className="font-semibold text-blue-500">
                {formatted.expectedGoals.away.toFixed(2)}
              </span>
            </div>
            
            {/* Barra bidirecional EM AZUL */}
            <div className="relative h-1.5 sm:h-2 bg-muted rounded-full overflow-hidden flex">
              <div 
                className="h-full bg-blue-500 transition-all"
                style={{ 
                  width: `${(formatted.expectedGoals.home / (formatted.expectedGoals.home + formatted.expectedGoals.away || 1)) * 100}%` 
                }}
              />
              <div 
                className="h-full bg-blue-300 transition-all"
                style={{ 
                  width: `${(formatted.expectedGoals.away / (formatted.expectedGoals.home + formatted.expectedGoals.away || 1)) * 100}%` 
                }}
              />
            </div>
          </div>
        )}

        {/* Posse de Bola — SEMPRE PRIMEIRO */}
        <div className="space-y-2">
          <div className="flex justify-between text-xs sm:text-sm">
            <span className="font-medium">{formatted.possession.home}%</span>
            <span className="text-muted-foreground">Possession</span>
            <span className="font-medium">{formatted.possession.away}%</span>
          </div>
          <div className="relative h-2.5 sm:h-3 bg-muted rounded-full overflow-hidden flex">
            <div 
              className="h-full bg-green-500 transition-all"
              style={{ width: `${formatted.possession.home}%` }}
            />
            <div 
              className="h-full bg-red-500 transition-all"
              style={{ width: `${formatted.possession.away}%` }}
            />
          </div>
        </div>

        {/* Goals Prevented - só aparece se existirem dados */}
        {formatted.goalsPrevented && (formatted.goalsPrevented.home > 0 || formatted.goalsPrevented.away > 0) && (
          <StatRow 
            label="Goals Prevented" 
            homeValue={formatted.goalsPrevented.home} 
            awayValue={formatted.goalsPrevented.away} 
          />
        )}
        
        <StatRow 
          label="Shots" 
          homeValue={formatted.shots.home} 
          awayValue={formatted.shots.away} 
        />
        <StatRow 
          label="Shots on Target" 
          homeValue={formatted.shots.homeOnTarget} 
          awayValue={formatted.shots.awayOnTarget} 
        />
        <StatRow 
          label="Shots off Goal" 
          homeValue={formatted.shotsOffGoal.home} 
          awayValue={formatted.shotsOffGoal.away} 
        />
        <StatRow 
          label="Blocked Shots" 
          homeValue={formatted.blockedShots.home} 
          awayValue={formatted.blockedShots.away} 
        />
        <StatRow 
          label="Shots Inside Box" 
          homeValue={formatted.shotsInsideBox.home} 
          awayValue={formatted.shotsInsideBox.away} 
        />
        <StatRow 
          label="Shots Outside Box" 
          homeValue={formatted.shotsOutsideBox.home} 
          awayValue={formatted.shotsOutsideBox.away} 
        />
        <StatRow 
          label="Offsides"
          homeValue={formatted.offsides.home} 
          awayValue={formatted.offsides.away} 
        />
        <StatRow 
          label="Corners" 
          homeValue={formatted.corners.home} 
          awayValue={formatted.corners.away} 
        />
        <StatRow 
          label="GK Saves" 
          homeValue={formatted.saves.home} 
          awayValue={formatted.saves.away} 
        />
        <StatRow 
          label="Fouls" 
          homeValue={formatted.fouls.home} 
          awayValue={formatted.fouls.away} 
        />
        
        {/* Passes com percentagem */}
        <div className="space-y-2">
          <div className="flex justify-between text-xs sm:text-sm">
            <span className="font-medium">{formatted.passes.home.percentage}%</span>
            <span className="text-muted-foreground">Passes</span>
            <span className="font-medium">{formatted.passes.away.percentage}%</span>
          </div>
          <div className="flex justify-between text-xs text-muted-foreground">
            <span>({formatted.passes.home.accurate}/{formatted.passes.home.total})</span>
            <span>({formatted.passes.away.accurate}/{formatted.passes.away.total})</span>
          </div>
          <div className="relative h-2.5 sm:h-3 bg-muted rounded-full overflow-hidden flex">
            <div 
              className="h-full bg-green-500 transition-all"
              style={{ width: `${formatted.passes.home.percentage}%` }}
            />
            <div 
              className="h-full bg-red-500 transition-all"
              style={{ width: `${formatted.passes.away.percentage}%` }}
            />
          </div>
        </div>
        
        {/* Cartões */}
        <div className="pt-2 border-t space-y-2">
          <div className="flex justify-between text-xs sm:text-sm">
            <div className="flex gap-2">
              <span className="font-medium">{formatted.cards.homeYellow}</span>
              {formatted.cards.homeRed > 0 && (
                <span className="font-medium text-red-500">{formatted.cards.homeRed}</span>
              )}
            </div>
            <span className="text-muted-foreground">Cards</span>
            <div className="flex gap-2">
              {formatted.cards.awayRed > 0 && (
                <span className="font-medium text-red-500">{formatted.cards.awayRed}</span>
              )}
              <span className="font-medium">{formatted.cards.awayYellow}</span>
            </div>
          </div>
          
          <div className="flex gap-2">
            <div className="flex-1 flex justify-start items-center gap-1">
              {Array.from({ length: formatted.cards.homeYellow }).map((_, i) => (
                <div key={`yellow-home-${i}`} className="w-2 h-3 sm:w-3 sm:h-4 bg-yellow-500 rounded-sm" />
              ))}
              {Array.from({ length: formatted.cards.homeRed }).map((_, i) => (
                <div key={`red-home-${i}`} className="w-2 h-3 sm:w-3 sm:h-4 bg-red-500 rounded-sm" />
              ))}
            </div>
            <div className="flex-1 flex justify-end items-center gap-1">
              {Array.from({ length: formatted.cards.awayRed }).map((_, i) => (
                <div key={`red-away-${i}`} className="w-2 h-3 sm:w-3 sm:h-4 bg-red-500 rounded-sm" />
              ))}
              {Array.from({ length: formatted.cards.awayYellow }).map((_, i) => (
                <div key={`yellow-away-${i}`} className="w-2 h-3 sm:w-3 sm:h-4 bg-yellow-500 rounded-sm" />
              ))}
            </div>
          </div>
        </div>

        {/* Advanced Statistics — conditional on Sportmonks enrichment */}
        {formatted.advanced && (
          <div className="pt-4 border-t border-border space-y-4">
            <p className="text-xs sm:text-sm font-semibold text-center text-muted-foreground">
              ⚡ {t('stats.advancedStats', 'Advanced Statistics')}
            </p>
            <StatRow 
              label={t('stats.attacks', 'Attacks')}
              homeValue={formatted.advanced.attacks.home}
              awayValue={formatted.advanced.attacks.away}
            />
            <StatRow 
              label={t('stats.dangerousAttacks', 'Dangerous Attacks')}
              homeValue={formatted.advanced.dangerousAttacks.home}
              awayValue={formatted.advanced.dangerousAttacks.away}
            />
            <StatRow 
              label={t('stats.tackles', 'Tackles')}
              homeValue={formatted.advanced.tackles.home}
              awayValue={formatted.advanced.tackles.away}
            />
            <StatRow 
              label={t('stats.interceptions', 'Interceptions')}
              homeValue={formatted.advanced.interceptions.home}
              awayValue={formatted.advanced.interceptions.away}
            />
            <StatRow 
              label={t('stats.keyPasses', 'Key Passes')}
              homeValue={formatted.advanced.keyPasses.home}
              awayValue={formatted.advanced.keyPasses.away}
            />
            <StatRow 
              label={t('stats.bigChancesCreated', 'Big Chances Created')}
              homeValue={formatted.advanced.bigChancesCreated.home}
              awayValue={formatted.advanced.bigChancesCreated.away}
            />
            <StatRow 
              label={t('stats.bigChancesMissed', 'Big Chances Missed')}
              homeValue={formatted.advanced.bigChancesMissed.home}
              awayValue={formatted.advanced.bigChancesMissed.away}
            />
            <StatRow 
              label={t('stats.duelsWon', 'Duels Won')}
              homeValue={formatted.advanced.duelsWon.home}
              awayValue={formatted.advanced.duelsWon.away}
            />
            <StatRow 
              label={t('stats.crosses', 'Crosses')}
              homeValue={formatted.advanced.totalCrosses.home}
              awayValue={formatted.advanced.totalCrosses.away}
            />
            <StatRow 
              label={t('stats.accurateCrosses', 'Accurate Crosses')}
              homeValue={formatted.advanced.accurateCrosses.home}
              awayValue={formatted.advanced.accurateCrosses.away}
            />
            <StatRow 
              label={t('stats.dribbles', 'Dribbles')}
              homeValue={formatted.advanced.dribbleAttempts.home}
              awayValue={formatted.advanced.dribbleAttempts.away}
            />
            <StatRow 
              label={t('stats.successfulDribbles', 'Successful Dribbles')}
              homeValue={formatted.advanced.successfulDribbles.home}
              awayValue={formatted.advanced.successfulDribbles.away}
            />
            <StatRow 
              label={t('stats.clearances', 'Clearances')}
              homeValue={formatted.advanced.clearances.home}
              awayValue={formatted.advanced.clearances.away}
            />
            <StatRow 
              label={t('stats.longBalls', 'Long Balls')}
              homeValue={formatted.advanced.longBalls.home}
              awayValue={formatted.advanced.longBalls.away}
            />
            <StatRow 
              label={t('stats.throwIns', 'Throw-ins')}
              homeValue={formatted.advanced.throwIns.home}
              awayValue={formatted.advanced.throwIns.away}
            />
            <StatRow 
              label={t('stats.freeKicks', 'Free Kicks')}
              homeValue={formatted.advanced.freeKicks.home}
              awayValue={formatted.advanced.freeKicks.away}
            />
            <StatRow 
              label={t('stats.goalKicks', 'Goal Kicks')}
              homeValue={formatted.advanced.goalKicks.home}
              awayValue={formatted.advanced.goalKicks.away}
            />
            <StatRow 
              label={t('stats.foulsWon', 'Fouls Won')}
              homeValue={formatted.advanced.foulsWon.home}
              awayValue={formatted.advanced.foulsWon.away}
            />
            <StatRow 
              label={t('stats.hitWoodwork', 'Hit Woodwork')}
              homeValue={formatted.advanced.hitWoodwork.home}
              awayValue={formatted.advanced.hitWoodwork.away}
            />
            <StatRow 
              label={t('stats.totalDuels', 'Total Duels')}
              homeValue={formatted.advanced.totalDuels.home}
              awayValue={formatted.advanced.totalDuels.away}
            />
            <StatRow 
              label={t('stats.counterAttacks', 'Counter Attacks')}
              homeValue={formatted.advanced.counterAttacks.home}
              awayValue={formatted.advanced.counterAttacks.away}
            />
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <StatsPeriodFilter period={period} onChange={setPeriod} />
        
        {isLive && period === 'game' && (
          <Badge variant="outline" className="text-xs">
            <span className="inline-block w-2 h-2 bg-red-500 rounded-full mr-1 animate-pulse" />
            Live • {new Date(dataUpdatedAt).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}
          </Badge>
        )}
      </div>
      
      <div className="flex justify-between items-center text-xs">
        <div className="flex items-center gap-2">
          {homeTeamLogo && (
            <img 
              src={homeTeamLogo} 
              alt={homeTeamName}
              className="w-6 h-6 object-contain"
            />
          )}
          <span className="font-medium">{homeTeamName}</span>
        </div>
        
        <div className="flex items-center gap-2 flex-row-reverse">
          {awayTeamLogo && (
            <img 
              src={awayTeamLogo} 
              alt={awayTeamName}
              className="w-6 h-6 object-contain"
            />
          )}
          <span className="font-medium">{awayTeamName}</span>
        </div>
      </div>

      {isHalfView ? renderHalfStats() : renderFullGameStats()}
    </div>
  );
}
